import { merge } from 'lodash';
import { useCallback } from 'react';
import { AppFeatureKey } from '../../components/feature/feature.types';
import {
  useGetUserPreference,
  useGetUserPreferenceWithLoading,
  useSetUserPreferenceLazy,
} from './preference.hooks';

const FEATURE_TOUR_PREFERENCE_KEY = 'featureTour';

interface FeatureTourRecord {
  seen?: boolean;
  skipped?: boolean;
}

type FeatureTourPreference = Partial<Record<AppFeatureKey, FeatureTourRecord>>;

type GetFeatureTourPreference = (
  appFeatureKey: AppFeatureKey
) => FeatureTourRecord | undefined;

const useGetFeatureTourPreferenceLazy = () => {
  const { preference: featureTourPreferences, loading } =
    useGetUserPreferenceWithLoading<FeatureTourPreference>(
      FEATURE_TOUR_PREFERENCE_KEY
    );

  const getFeatureTourPreference: GetFeatureTourPreference = useCallback(
    (appFeatureKey: AppFeatureKey): FeatureTourRecord | undefined => {
      if (loading) {
        return undefined;
      }

      return featureTourPreferences?.value[appFeatureKey] ?? {};
    },
    [featureTourPreferences?.value, loading]
  );

  return getFeatureTourPreference;
};

const useGetFeatureTourPreference = (
  appFeatureKey: AppFeatureKey
): FeatureTourRecord | undefined => {
  const getFeatureTourPreference = useGetFeatureTourPreferenceLazy();

  return getFeatureTourPreference(appFeatureKey);
};

type SetFeatureTourPreference = (
  appFeatureKey: AppFeatureKey,
  value: Partial<FeatureTourRecord>
) => void;

const useSetFeatureTourPreference = (): SetFeatureTourPreference => {
  const featureTourPreferences = useGetUserPreference<FeatureTourPreference>(
    FEATURE_TOUR_PREFERENCE_KEY
  );

  const [setUserPreference] = useSetUserPreferenceLazy<FeatureTourPreference>();

  const setFeatureTourPreference = useCallback(
    (appFeatureKey: AppFeatureKey, value: Partial<FeatureTourRecord>) => {
      const mergedFeatureTourPreference: FeatureTourPreference = merge(
        {},
        featureTourPreferences?.value,
        {
          [appFeatureKey]: value,
        }
      );

      setUserPreference({
        key: FEATURE_TOUR_PREFERENCE_KEY,
        value: mergedFeatureTourPreference,
      });
    },
    [featureTourPreferences?.value, setUserPreference]
  );

  return setFeatureTourPreference;
};

export {
  useGetFeatureTourPreference,
  useSetFeatureTourPreference,
  useGetFeatureTourPreferenceLazy,
};
